'use client'

import { useState, useEffect } from 'react'

/**
 * Hook to detect document direction (LTR/RTL)
 * Reads from document.documentElement.dir and updates when it changes
 *
 * @returns 'rtl' | 'ltr'
 */
export function useDirection(): 'rtl' | 'ltr' {
  const [direction, setDirection] = useState<'rtl' | 'ltr'>('ltr')

  useEffect(() => {
    // Initial read
    const getDirection = () =>
      (document.documentElement.dir as 'rtl' | 'ltr') || 'ltr'

    setDirection(getDirection())

    // Watch for changes using MutationObserver
    const observer = new MutationObserver(() => {
      setDirection(getDirection())
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir'],
    })

    return () => observer.disconnect()
  }, [])

  return direction
}

/**
 * Hook to check if current direction is RTL
 * @returns boolean - true if RTL
 */
export function useIsRTLDirection(): boolean {
  return useDirection() === 'rtl'
}
